'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import { ChevronRight, ChevronLeft, User, Lightbulb, Sparkles, Info, LogIn } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useLanguage } from '@/context/LanguageContext';
import CountrySelectModal, { CountryBadge, useCountry } from '@/components/CountrySelectModal';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { getContextualTips, TipContext } from '@/app/lib/tips-data';
import { usePathname, useRouter } from 'next/navigation';

export default function Navigation({ title }: { title: string }) {
  const { isRTL, lang } = useLanguage();
  const pathname = usePathname();
  const router = useRouter();
  const { country, setCountry } = useCountry();
  const [countryModalOpen, setCountryModalOpen] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  useEffect(() => {
    setIsLoggedIn(!!localStorage.getItem('s360_access'));
  }, []);

  const getContext = (): TipContext => {
    if (pathname.includes('marketplace') || pathname.includes('buy')) return 'buy';
    if (pathname.includes('sell')) return 'sell';
    if (pathname.includes('inspection') || pathname.includes('diagnose')) return 'inspection';
    if (pathname.includes('parts')) return 'parts';
    return 'general';
  };

  const context = getContext();

  return (
    <nav className="flex items-center justify-between p-4 bg-white sticky top-0 z-50 shadow-sm md:hidden -mx-4 mb-4">
      <div className="flex items-center gap-3">
        <Button
          variant="ghost"
          size="icon"
          className="rounded-full hover:bg-slate-100"
          onClick={() => router.back()}
        >
          {isRTL ? (
            <ChevronRight className="h-6 w-6 text-slate-600" />
          ) : (
            <ChevronLeft className="h-6 w-6 text-slate-600" />
          )}
        </Button>
        <h1 className="text-lg font-extrabold text-slate-800">{title}</h1>
      </div>

      <div className="flex items-center gap-2">
        <Dialog>
          <DialogTrigger asChild>
            <Button variant="ghost" size="sm" className="rounded-full h-10 px-3 flex gap-2 text-primary hover:bg-slate-50 border border-slate-100 shadow-sm transition-all group">
              <Lightbulb className="h-4 w-4 group-hover:fill-primary" />
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-[2.5rem] p-8 max-w-md border-none shadow-2xl">
            <DialogHeader className="mb-6">
              <DialogTitle className="text-2xl font-black flex items-center gap-3 text-slate-800">
                <div className="p-3 bg-indigo-50 rounded-2xl">
                  <Sparkles className="h-6 w-6 text-primary" />
                </div>
                {lang === 'ar' ? 'نصيحة ومقارنة ذكية' : 'Smart Tip & Comparison'}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              {getContextualTips(context, 3).map((tip, idx) => (
                <div key={tip.id} className="flex gap-4 p-4 bg-slate-50 rounded-2xl border border-slate-100 animate-in fade-in slide-in-from-bottom-2" style={{ animationDelay: `${idx * 100}ms` }}>
                  <div className="h-8 w-8 rounded-full bg-white shadow-sm flex items-center justify-center shrink-0">
                    <Info className="h-4 w-4 text-primary" />
                  </div>
                  <p className="text-sm font-bold text-slate-700 leading-relaxed">{tip.text_ar}</p>
                </div>
              ))}

              <div className="mt-4 p-4 bg-blue-600 rounded-2xl text-white">
                <p className="text-[10px] font-bold uppercase tracking-widest opacity-80 mb-1">مقارنة الخبير:</p>
                <p className="text-xs font-medium">
                  {context === 'buy' && "مقارنة: السيارات اليابانية توفر 20% في استهلاك الوقود مقارنة بالأمريكية في نفس الفئة."}
                  {context === 'sell' && "مقارنة: السيارات المعروضة بتقرير فحص جاهز تُباع أسرع بـ 3 مرات من غيرها."}
                  {context === 'parts' && "مقارنة: القطع الأصلية أغلى بـ 30% لكنها تدوم 3 أضعاف عمر القطع التجارية."}
                  {context === 'inspection' && "مقارنة: الفحص البصري يكتشف 80% من المشاكل الخارجية، بينما الفحص الميكانيكي يضمن السلامة الداخلية."}
                  {context === 'general' && "مقارنة: الصيانة الدورية في موعدها توفر 40% من تكاليف الإصلاحات الكبرى مستقبلاً."}
                </p>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        <Link href={isLoggedIn ? '/customer-dashboard' : '/login'}>
          <Button variant="ghost" size="icon" className="rounded-full hover:bg-blue-50 text-slate-600 hover:text-blue-600">
            {isLoggedIn ? <User className="h-5 w-5" /> : <LogIn className="h-5 w-5" />}
          </Button>
        </Link>
        <CountryBadge onClick={() => setCountryModalOpen(true)} />
      </div>

      <CountrySelectModal
        isOpen={countryModalOpen}
        onClose={() => setCountryModalOpen(false)}
        onSelect={setCountry}
        showAllOption
      />
    </nav>
  );
}
